import React, { useEffect, useState } from "react";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Collapse, Divider, Form, Input, InputNumber, Typography } from "antd";
import { type FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

import { ClientProfileGoal } from "@fitness-app/data-models/entities/ClientProfile";
import { DietaryPreference, type MealsPlanWithCreator } from "@fitness-app/data-models/entities/MealsPlan";
import { type ProgramAutomation } from "@fitness-app/data-models/entities/ProgramAutomation";

import {
  MealsPlanTemplateFilterFields,
  MealsPlanTemplateSelect,
} from "~/modules/Nutrition/components/MealsPlanTemplateSelect";
import {
  DEFAULT_MEALS_PLAN_TEMPLATE_FILTER,
  useMealsPlanTemplateSearch,
} from "~/modules/Nutrition/hooks/useMealsPlanTemplateSearch";
import { type ScheduledNutritionFormModel } from "./types";

interface ScheduledNutritionFormProps {
  formController?: FormInstance<ScheduledNutritionFormModel>;
  onSubmit: (formData: ScheduledNutritionFormModel, mealsPlanById: Record<string, MealsPlanWithCreator>) => void;
  model?: Partial<ScheduledNutritionFormModel> | null;
  selectedAutomation: ProgramAutomation;
  withoutWeeksLimit?: boolean;
}

const DIETARY_PREFERENCES = [
  DietaryPreference.NoPreference,
  DietaryPreference.SemiVegetarian,
  DietaryPreference.Vegetarian,
  DietaryPreference.Vegan,
] as const;

export const GOAL_LABELS: Record<ClientProfileGoal, string> = {
  [ClientProfileGoal.WeightLoss]: "Cel: schudnąć",
  [ClientProfileGoal.Recomposition]: "Cel: wyrzeźbić sylwetkę",
  [ClientProfileGoal.Energy]: "Cel: energia i kondycja",
  [ClientProfileGoal.Health]: "Cel: zdrowie",
};

const ScheduledNutritionForm = ({
  model,
  onSubmit,
  formController,
  selectedAutomation,
  withoutWeeksLimit = false,
}: ScheduledNutritionFormProps) => {
  const { t } = useTranslation(["automation", "common"]);
  const ranges = Form.useWatch("ranges", formController);
  const [filter, setFilter] = useState(DEFAULT_MEALS_PLAN_TEMPLATE_FILTER);
  const { plans, plansById, isLoading, onSearch } = useMealsPlanTemplateSearch(filter);
  const currentNumberOfScheduledWeeks = Object.keys(selectedAutomation.schedule).length;

  useEffect(() => {
    if (model) {
      formController?.setFieldsValue(model);
    } else {
      const lastItem = selectedAutomation.scheduledNutritionPlan?.at(-1);
      formController?.setFieldsValue({
        startWeek: lastItem ? (lastItem?.endWeek || 1) + 1 : 1,
      });
    }
  }, [model]);

  return (
    <Form<ScheduledNutritionFormModel>
      name="form"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 14 }}
      layout="horizontal"
      form={formController}
      initialValues={{
        startWeek: 1,
        endWeek: currentNumberOfScheduledWeeks,
        additionalComment: "",
        ranges: [],
      }}
      onFinish={(formData) => onSubmit(formData, plansById)}
    >
      <>
        <Form.Item
          name="startWeek"
          label={t<string>("nutrition.form.from")}
          rules={[
            {
              required: true,
              message: t<string>("common:validationErrors.fieldIsRequired"),
            },
          ]}
        >
          <InputNumber min={1} max={withoutWeeksLimit ? undefined : currentNumberOfScheduledWeeks} />
        </Form.Item>

        <Form.Item name="endWeek" label={t<string>("nutrition.form.to")}>
          <InputNumber min={1} max={withoutWeeksLimit ? undefined : currentNumberOfScheduledWeeks} />
        </Form.Item>
      </>

      <MealsPlanTemplateFilterFields value={filter} onChange={setFilter} />

      <Form.Item label={t<string>("nutrition.form.ranges")}>
        <Form.List name="ranges">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <div key={field.key} className="flex w-full flex-col">
                  <div className="flex gap-x-4">
                    <Form.Item
                      name={[field.name, "from"]}
                      rules={[
                        {
                          required: index !== 0,
                          message: t<string>("common:validationErrors.fieldIsRequired"),
                        },
                      ]}
                    >
                      <InputNumber
                        min={index === 0 ? undefined : (ranges[index - -1]?.to ?? undefined)}
                        placeholder={t<string>("nutrition.form.fromPlaceholder")}
                        addonAfter="kg"
                      />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "to"]}
                      rules={[
                        {
                          required: ranges?.length - 1 !== index,
                          message: t<string>("common:validationErrors.fieldIsRequired"),
                        },
                      ]}
                    >
                      <InputNumber placeholder={t<string>("nutrition.form.toPlaceholder")} addonAfter="kg" />
                    </Form.Item>
                  </div>

                  {DIETARY_PREFERENCES.map((preference) => (
                    <Form.Item
                      key={preference}
                      rules={
                        preference === DietaryPreference.NoPreference
                          ? [
                              {
                                required: true,
                                message: t<string>("common:validationErrors.fieldIsRequired"),
                              },
                            ]
                          : undefined
                      }
                      labelAlign="left"
                      labelCol={{ span: 10 }}
                      label={t(`nutrition:dietaryPreferenceEnum.${preference}`)}
                      name={[field.name, "nutrition", preference]}
                      validateStatus={isLoading ? "validating" : undefined}
                    >
                      <MealsPlanTemplateSelect
                        plans={plans}
                        onSearch={onSearch}
                        loading={isLoading}
                        disabled={!!model}
                        placeholder={t<string>("nutrition.form.selectNutrition")}
                      />
                    </Form.Item>
                  ))}

                  <Collapse ghost className="mb-2">
                    <Collapse.Panel key="byGoal" header="Warianty wg celu z ankiety (opcjonalnie)" forceRender>
                      <Typography.Paragraph type="secondary">
                        Jeśli klientka ma w profilu dany cel, plan z wariantu nadpisze plan bazowy dla wskazanej
                        preferencji. Puste pola = plan bazowy.
                      </Typography.Paragraph>
                      {Object.values(ClientProfileGoal).map((goal) => (
                        <div key={goal}>
                          <Typography.Text strong>{GOAL_LABELS[goal]}</Typography.Text>
                          {DIETARY_PREFERENCES.map((preference) => (
                            <Form.Item
                              key={`${goal}-${preference}`}
                              labelAlign="left"
                              labelCol={{ span: 10 }}
                              label={t(`nutrition:dietaryPreferenceEnum.${preference}`)}
                              name={[field.name, "nutritionByGoal", goal, preference]}
                            >
                              <MealsPlanTemplateSelect
                                plans={plans}
                                onSearch={onSearch}
                                loading={isLoading}
                                disabled={!!model}
                                placeholder="Jak plan bazowy"
                              />
                            </Form.Item>
                          ))}
                        </div>
                      ))}
                    </Collapse.Panel>
                  </Collapse>

                  <MinusCircleOutlined
                    className="text-red-500"
                    onClick={() => remove(field.name)}
                    style={{ marginLeft: 12 }}
                  />
                  <Divider />
                </div>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      from: ranges.at(-1)?.to || null,
                      to: null,
                      nutrition: { 1: null, 2: null, 3: null, 4: null },
                    })
                  }
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 10 }}
                >
                  {t("common:button.add")}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>

      <Form.Item name="additionalComment" label={t("nutrition.form.additionalComment")}>
        <Input.TextArea />
      </Form.Item>
    </Form>
  );
};

export default ScheduledNutritionForm;
